import React, { CSSProperties, MouseEvent } from 'react'
import {
  Avatar as MaterialAvatar,
  Icon,
  Typography,
  IconButton,
} from '@mui/material'
import { CssStyles, IconName } from '../../types'
import { getClassName, getCSSSize, parseClassNames } from '../../utils'
import { cx } from '@emotion/css'

export interface IAvatar {
  /** Source of the image to display */
  imageSrc?: string

  /** Text to display */
  text?: string

  /** If true, provided text will be transformed to initials. */
  displayInitials?: boolean

  /** The icon to display if no image or text is specified
   * @default account_circle
   */
  icon?: IconName

  /** The shape of the avatar
   * @default circular
   */
  variant?: 'circular' | 'rounded' | 'square'

  /** The size of the avatar */
  size?: string | number

  /** The size of the text */
  fontSize?: string | number

  /** Foreground color */
  color?: string

  /** Background color */
  backgroundColor?: string

  /** Provide an optional function to be called when the avatar is clicked */
  onClick?: (event: MouseEvent) => void

  /** *Deprecated* Raw CSS styles
   * @deprecated
   */
  styles?: CssStyles<'root' | 'icon' | 'text' | 'button'>

  style?: CSSProperties | string
  iconStyle?: CSSProperties | string
  textStyle?: CSSProperties | string
  buttonStyle?: CSSProperties | string
}

const getInitials = (text?: string) =>
  text
    ?.split(' ')
    .map((item) => item[0]?.toUpperCase())
    .join('')

export const Avatar = React.forwardRef<HTMLButtonElement, IAvatar>(
  (
    {
      imageSrc,
      text,
      displayInitials,
      size,
      fontSize,
      icon = 'account_circle',
      variant = 'circular',
      color,
      backgroundColor,
      styles,
      onClick,
      style,
      iconStyle,
      textStyle,
      buttonStyle,
    },
    ref
  ) => {
    const sizeValue = getCSSSize(size, '40px')
    const fontSizeValue = getCSSSize(fontSize, 'auto')

    const sizeStyle = {
      width: sizeValue,
      height: sizeValue,
    }

    const classNames = styles && parseClassNames(styles)

    return (
      <IconButton
        ref={ref}
        sx={{
          ...sizeStyle,
        }}
        className={cx(classNames?.button, getClassName(buttonStyle))}
        onClick={onClick}
        disabled={!onClick}
      >
        <MaterialAvatar
          src={imageSrc}
          variant={variant}
          sx={{
            color: color ?? '#ffffff',
            bgcolor: backgroundColor ?? 'primary.main',
            ...sizeStyle,
          }}
          className={cx(classNames?.root, getClassName(style))}
        >
          {text && !imageSrc && (
            <Typography
              sx={{ fontSize: fontSizeValue }}
              className={cx(classNames?.text, getClassName(textStyle))}
            >
              {displayInitials ? getInitials(text) : text}
            </Typography>
          )}
          {!text && !imageSrc && (
            <Icon
              className={cx(classNames?.icon, getClassName(iconStyle))}
              sx={{
                '&.material-icons': {
                  fontSize: fontSizeValue,
                },
              }}
            >
              {icon}
            </Icon>
          )}
        </MaterialAvatar>
      </IconButton>
    )
  }
)

if (process.env.NODE_ENV !== 'production') {
  Avatar.displayName = 'Avatar'
}
